import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Icon from '@components/atoms/Icon'
import ColorDot from '@components/atoms/ColorDot'
import { Swiper, SwiperSlide } from '@components/molecules/Slider'
import { useSelector } from 'react-redux'
import { RootState } from '@redux/store'

const createSvgDataUri = (svg: string): string => `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`

const DEFAULT_EYEWEAR_FIT_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" version="1.0" width="32px" height="16px" viewBox="0 0 1280 640" preserveAspectRatio="xMidYMid meet"> <g transform="translate(0,640) scale(0.1,-0.1)" fill="black" stroke="none"> <path d="M2335 5419 c-360 -23 -797 -95 -1450 -239 -99 -22 -331 -69 -515 -106 -184 -37 -340 -68 -346 -70 -15 -3 -29 -339 -21 -469 13 -200 45 -273 189 -438 245 -281 285 -348 342 -582 128 -521 331 -1112 545 -1584 133 -295 311 -475 636 -642 554 -286 1278 -380 2055 -269 475 69 861 268 1205 621 245 252 440 538 650 954 107 211 139 297 220 600 66 245 100 346 161 470 86 179 138 216 309 223 55 2 161 -1 235 -6 161 -11 191 -23 271 -109 105 -114 171 -251 259 -543 217 -719 484 -1209 851 -1565 174 -168 310 -265 518 -369 406 -204 854 -306 1346 -306 910 0 1659 377 2041 1027 136 230 208 434 354 1003 171 666 214 794 320 950 45 66 126 143 197 188 88 56 88 55 86 422 0 85 0 189 1 230 2 138 -44 177 -257 216 -171 32 -290 60 -572 138 -327 91 -379 103 -654 146 -683 108 -863 125 -1230 117 -488 -11 -826 -59 -1826 -257 -675 -134 -955 -181 -1278 -215 -248 -26 -554 -36 -773 -25 -392 19 -583 48 -1179 180 -550 122 -661 143 -1031 195 -786 109 -1239 140 -1659 114z"/> </g> </svg>`
const DEFAULT_STANDARD_FIT_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none"><path d="M18.7502 0H6.25006C1.87619 1.41663 0 7.36434 0 7.36434L4.20033 10.1003L5.41817 8.8618V25H19.5818V8.85251L20.7997 10.1004L25 7.36443C25 7.36443 23.1241 1.41672 18.7502 0ZM9.17391 0.417126H15.8387C15.8387 0.417126 15.7353 3.46473 12.4998 3.46473C9.26437 3.46473 9.17362 0.417126 9.17362 0.417126H9.17391ZM17.0819 9.60823C16.8108 10.3874 16.0881 10.9077 15.2781 10.9077C14.467 10.9077 13.7444 10.3874 13.4732 9.60823V6.56422H17.0821L17.0819 9.60823Z" fill="black"/></svg>`

const DEFAULT_APPAREL_FIT_OPTIONS = [
  'Run Small',
  'Run Slightly Small',
  'True to Size',
  'Run Slightly Large',
  'Run Large',
] as const

const DEFAULT_EYEWEAR_FIT_OPTIONS = ['Narrowest', 'Narrow', 'Regular', 'Wide', 'Widest'] as const

const SIDE_VIEW_KEYWORDS = ['even_more_side_view']
const FRONT_VIEW_KEYWORDS = ['full_frontal']

const normalizeVariantId = (value: string | number | null | undefined): string | null => {
  if (value == null) return null
  const raw = String(value)
  if (!raw) return null
  if (raw.includes('/')) {
    const parts = raw.split('/')
    return parts[parts.length - 1] || raw
  }
  return raw
}

const getVariantLabelFromAlt = (alt?: string | null): string | null => {
  if (!alt) return null
  const [firstPart] = alt.split('|')
  if (!firstPart) return null
  const trimmed = firstPart.trim()
  if (!trimmed) return null
  const cleaned = /^[^a-zA-Z0-9]/.test(trimmed) ? trimmed.replace(/^[^a-zA-Z0-9]+/, '').trim() : trimmed
  return cleaned || null
}

export type InputData = {
  columns?: string[]
  entryLabel?: string[]
  entrySizes?: string[]
  apparelFit?: string
  size?: string
  image?: string
  image_secondary?: string
  frame_width?: { measurement: string }
  frame_height?: { measurement: string }
  nose_opening?: { measurement: string }
  apparelFitOptions?: string[] | string
  apparelFitIcon?: string | null
  frameHeight?: string | number | null
  frameWidth?: string | number | null
  bridgeWidth?: string | number | null
  templeLength?: string | number | null
  templeLengthMax?: string | number | null
  eyewearFit?: string | null
  eyewearFitOptions?: string[] | string
  eyewearFitIcon?: string | null
}

type VariantSizeGuideEntry = {
  id: number | string
  sizeGuide: InputData | null
}

type VariantSizeOption = {
  id: number | string
  value?: string | null
  title?: string | null
  available?: boolean
}

type ProductMediaItem = { id?: number | string; src: string; alt?: string | null }

export type OutputData = {
  data: {
    size: string
    [key: string]: { measurement: string } | string
  }[]
}

export interface SizeGuideDrawerProps {
  title: string
  titleIcon?: string | null
  titleTextarea?: string | null
  productTitle: string
  ctaText?: string
  ctaLink?: string
  ctaVariant?: string
  isEyewear?: boolean
  isGoggle?: boolean
  productMedia?: ProductMediaItem[] | null
  productSizeGuide?: InputData | null
  variantSizeGuides?: VariantSizeGuideEntry[] | null
  initialVariantId?: number | string | null
  variantSizeOptions?: VariantSizeOption[] | null
}

const TRIGGER_SELECTOR = '[data-size-guide-drawer-trigger], a[href="#size-guide-drawer-trigger"]'
const getVariantIdFromUrl = (): string | null => {
  if (typeof window === 'undefined') return null
  return new URLSearchParams(window.location.search).get('variant')
}

const SizeGuideDrawer: React.FC<SizeGuideDrawerProps> = ({
  title,
  titleIcon,
  titleTextarea,
  productTitle,
  productSizeGuide,
  ctaText,
  ctaLink,
  ctaVariant,
  productMedia,
  isEyewear = false,
  isGoggle = false,
  variantSizeGuides = [],
  initialVariantId,
  variantSizeOptions = [],
}) => {
  const PDPVariant = useSelector((state: RootState) => state.PDP?.PDPVariant)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const [portalElement, setPortalElement] = useState<HTMLDivElement | null>(null)

  const heading = title && title.trim().length > 0 ? title : 'Size Guide'
  const titleIconSrc = titleIcon && titleIcon.trim().length > 0 ? titleIcon : null
  const headingTextarea = titleTextarea && titleTextarea.trim().length > 0 ? titleTextarea : 'Returns can be made within 30 days of the purchase date.'

  const variantGuidesMap = useMemo(() => {
    const map = new Map<string, InputData>()
    variantSizeGuides?.forEach((entry) => {
      if (!entry || entry.sizeGuide == null || entry.id == null) return
      const baseId = String(entry.id)
      map.set(baseId, entry.sizeGuide)
      if (!baseId.includes('gid://shopify/ProductVariant/')) {
        map.set(`gid://shopify/ProductVariant/${baseId}`, entry.sizeGuide)
      } else {
        const numericId = baseId.split('/').pop()
        if (numericId) {
          map.set(numericId, entry.sizeGuide)
        }
      }
    })
    return map
  }, [variantSizeGuides])

  const [activeVariantId, setActiveVariantId] = useState<string | null>(() => {
    const urlVariant = getVariantIdFromUrl()
    if (urlVariant) return urlVariant
    if (PDPVariant?.id) return String(PDPVariant.id)
    if (initialVariantId) return String(initialVariantId)
    return null
  })

  useEffect(() => {
    if (PDPVariant?.id) {
      setActiveVariantId(String(PDPVariant.id))
    }
  }, [PDPVariant?.id])

  useEffect(() => {
    const handleUrlChange = () => {
      const urlVariant = getVariantIdFromUrl()
      if (urlVariant) {
        setActiveVariantId(urlVariant)
      }
    }

    window.addEventListener('popstate', handleUrlChange)

    return () => {
      window.removeEventListener('popstate', handleUrlChange)
    }
  }, [])

  const sizeGuideData = useMemo(() => {
    if (activeVariantId) {
      const candidates = [activeVariantId]
      const numericId = activeVariantId.split('/').pop()
      if (numericId && numericId !== activeVariantId) {
        candidates.push(numericId)
      }

      for (const candidate of candidates) {
        if (variantGuidesMap.has(candidate)) {
          return variantGuidesMap.get(candidate) ?? null
        }
      }
    }
    return productSizeGuide ?? null
  }, [activeVariantId, variantGuidesMap, productSizeGuide])

  const guideColumns = useMemo(() => sizeGuideData?.columns ?? [], [sizeGuideData])
  const guideLabels = useMemo(() => sizeGuideData?.entryLabel ?? [], [sizeGuideData])
  const guideEntries = useMemo(() => sizeGuideData?.entrySizes ?? [], [sizeGuideData])

  const baseGuideImage = useMemo(() => {
    const image = sizeGuideData?.image
    if (typeof image === 'string' && image.trim().length > 0) {
      return image
    }
    return null
  }, [sizeGuideData])

  // const baseSecondaryGuideImage = useMemo(() => {
  //   const image = sizeGuideData?.image_secondary
  //   if (typeof image === 'string' && image.trim().length > 0) {
  //     return image
  //   }
  //   return null
  // }, [sizeGuideData])

  const guideFit = isEyewear ? sizeGuideData?.eyewearFit ?? null : sizeGuideData?.apparelFit ?? null
  const guideFitIcon = useMemo(() => {
    const icon = isEyewear ? sizeGuideData?.eyewearFitIcon : sizeGuideData?.apparelFitIcon
    if (typeof icon === 'string' && icon.trim().length > 0) {
      return icon
    }
    return null
  }, [isEyewear, sizeGuideData])

  const guideFitOptions = useMemo(() => {
    const options = isEyewear ? sizeGuideData?.eyewearFitOptions : sizeGuideData?.apparelFitOptions
    const fallback = isEyewear ? DEFAULT_EYEWEAR_FIT_OPTIONS : DEFAULT_APPAREL_FIT_OPTIONS

    if (!options) return [...fallback]

    const parsed =
      Array.isArray(options)
        ? options.map((value) => String(value).trim())
        : String(options).replace(/[\[\]\{\}]/g, '').split(',').map((value) => value.replace(/^"+|"+$/g, '').trim())

    const filtered = parsed.filter(Boolean)
    return filtered.length ? filtered : [...fallback]
  }, [isEyewear, sizeGuideData])

  const productNiceTitle = productTitle || heading
  const drawerCtaText = ctaText && ctaText.trim().length > 0 ? ctaText : null
  const drawerCtaLink = ctaLink && ctaLink.trim().length > 0 ? ctaLink : '#'
  const drawerCtaVariant = ctaVariant && ctaVariant.trim().length > 0 ? ctaVariant : 'secondary'

  const productMediaItems = useMemo<ProductMediaItem[]>(() => {
    if (!productMedia || !Array.isArray(productMedia)) return []
    return productMedia.filter((media) => media && typeof media.src === 'string' && media.src.trim().length > 0)
  }, [productMedia])

  const normalizedActiveVariantId = useMemo(() => normalizeVariantId(activeVariantId), [activeVariantId])

  const activeVariantTitle = useMemo(() => {
    if (PDPVariant?.title) return PDPVariant.title
    if (!normalizedActiveVariantId || !Array.isArray(variantSizeOptions)) return null

    const match = variantSizeOptions.find((option) => normalizeVariantId(option.id) === normalizedActiveVariantId)
    if (match?.title) return match.title

    return null
  }, [PDPVariant?.title, normalizedActiveVariantId, variantSizeOptions])

  const variantSpecificMedia = useMemo<ProductMediaItem[]>(() => {
    if (!isEyewear || !activeVariantTitle) return []
    const normalizedTitle = activeVariantTitle.trim().toLowerCase()
    if (!normalizedTitle) return []

    return productMediaItems.filter((media) => {
      if (!media || !media.alt) return false
      const variantLabel = getVariantLabelFromAlt(media.alt)
      if (!variantLabel) return media
      return variantLabel.toLowerCase() === normalizedTitle
    })
  }, [isEyewear, activeVariantTitle, productMediaItems])

  const { primaryVariantMedia, secondaryVariantMedia } = useMemo<{
    primaryVariantMedia: ProductMediaItem | null
    secondaryVariantMedia: ProductMediaItem | null
  }>(() => {
    // if (!isEyewear || variantSpecificMedia.length === 0) {
    //   return { primaryVariantMedia: null, secondaryVariantMedia: null }
    // }
    const sideViewMedia = variantSpecificMedia.find((media) => {
        const alt = media.alt?.toLowerCase() ?? ''
        return SIDE_VIEW_KEYWORDS.some((keyword) => alt.includes(keyword))
      }) ?? null

    const primaryMedia = variantSpecificMedia.find((media) => {
      const alt = media.alt?.toLowerCase() ?? ''
      return FRONT_VIEW_KEYWORDS.some((keyword) => alt.includes(keyword))
    }) ?? null

    let secondaryMedia: ProductMediaItem | null = null
    if (sideViewMedia && sideViewMedia !== primaryMedia) {
      secondaryMedia = sideViewMedia
    }

    return {
      primaryVariantMedia: primaryMedia ?? null,
      secondaryVariantMedia: secondaryMedia ?? null,
    }
  }, [isEyewear, variantSpecificMedia])

  const drawerCarouselMedia = useMemo(() => {
    if (variantSpecificMedia.length > 0) {
      return variantSpecificMedia
    }
    return productMediaItems
  }, [productMediaItems, variantSpecificMedia])

  const defaultGuideAltText = productNiceTitle ? `${productNiceTitle} size guide` : 'Size guide illustration'
  const guideImage = primaryVariantMedia?.src ?? baseGuideImage
  const guideImageAlt = primaryVariantMedia?.alt ?? defaultGuideAltText
  const secondaryGuideImage = secondaryVariantMedia?.src
  const secondaryGuideImageAlt = secondaryVariantMedia?.alt ?? defaultGuideAltText

  const guideImageWrapperClass = [
    'relative w-full',
    isEyewear ? 'aspect-[3/2] max-h-80' : 'h-[430px]',
  ].join(' ')

  const eyewearSizeOptions = useMemo(() => {
    if (!isEyewear || !Array.isArray(variantSizeOptions)) return []

    const seen = new Set<string>()

    return variantSizeOptions.reduce<{ id: string; label: string; available: boolean }[]>((acc, option) => {
      if (!option) return acc
      const optionId = option.id != null ? String(option.id) : ''
      const rawLabel = typeof option.value === 'string' ? option.value.trim() : ''
      if (!optionId || !rawLabel) return acc

      const normalizedLabel = rawLabel.toLowerCase()
      if (seen.has(normalizedLabel)) return acc
      seen.add(normalizedLabel)

      acc.push({
        id: optionId,
        label: rawLabel,
        available: option.available !== false,
      })

      return acc
    }, [])
  }, [isEyewear, variantSizeOptions])

  const parsedEntrySizes = useMemo(() => {
    return guideEntries.map((entry) => {
      if (!entry) return []
      if (Array.isArray(entry)) {
        return entry.map((value) => String(value).trim())
      }

      const normalized = String(entry).replace(/[\[\]\{\}]/g, '')

      return normalized
        .split(',')
        .map((value) => value.trim())
        .map((value) => value.replace(/^"+|"+$/g, '').trim())
        .filter(Boolean)
    })
  }, [guideEntries])

  const [selectedColumn, setSelectedColumn] = useState<string | null>(guideColumns[0] ?? null)
  const [measurementUnit, setMeasurementUnit] = useState<'in' | 'mm'>(() => (isEyewear ? 'mm' : 'in'))
  const measurementToggleUnits = useMemo(
    () => (isEyewear ? (['mm', 'in'] as const) : (['in', 'mm'] as const)),
    [isEyewear],
  )

  const measurementToggleActiveIndex = measurementToggleUnits.findIndex((unit) => unit === measurementUnit)
  const resolvedMeasurementToggleActiveIndex = measurementToggleActiveIndex < 0 ? 0 : measurementToggleActiveIndex
  const measurementToggleNextUnit =
    measurementToggleUnits.length > 1
      ? measurementToggleUnits[(resolvedMeasurementToggleActiveIndex + 1) % measurementToggleUnits.length]
      : measurementToggleUnits[resolvedMeasurementToggleActiveIndex] ?? measurementToggleUnits[0]

  const measurementToggleIndicatorAriaLabel = measurementToggleNextUnit
    ? `Switch to ${measurementToggleNextUnit === 'in' ? 'inch' : measurementToggleNextUnit}`
    : 'Toggle measurement unit'

  const handleMeasurementIndicatorToggle = useCallback(() => {
    if (!measurementToggleNextUnit || measurementToggleNextUnit === measurementUnit) return
    setMeasurementUnit(measurementToggleNextUnit)
  }, [measurementToggleNextUnit, measurementUnit])

  const mediaNavigationId = useMemo(() => Math.random().toString(36).slice(2, 10), [])
  const mediaPrevClass = `size-guide-media-prev-${mediaNavigationId}`
  const mediaNextClass = `size-guide-media-next-${mediaNavigationId}`

  const fallbackFitIcon = useMemo(() => {
    if (guideFitIcon && guideFitIcon.trim().length) {
      return guideFitIcon
    }
    const svg = isEyewear ? DEFAULT_EYEWEAR_FIT_ICON_SVG : DEFAULT_STANDARD_FIT_ICON_SVG
    return createSvgDataUri(svg)
  }, [guideFitIcon, isEyewear])

  useEffect(() => {
    if (!guideColumns.length) return

    if (isEyewear) {
      setSelectedColumn(null)
      return
    }

    const urlVariant = getVariantIdFromUrl()
    const variantMatch = urlVariant ? variantGuidesMap.get(urlVariant) : null

    if (variantMatch?.columns?.length) {
      const currentSizeOption =
        PDPVariant?.selectedOptions?.find((option) => option.name.toLowerCase() === 'size')?.value || null

      if (currentSizeOption) {
        const matchedColumnIndex = variantMatch.columns.findIndex(
          (column) => column.toLowerCase() === currentSizeOption.toLowerCase(),
        )

        if (matchedColumnIndex !== -1) {
          setSelectedColumn(variantMatch.columns[matchedColumnIndex])
          return
        }
      }

      setSelectedColumn(variantMatch.columns[0])

      return
    }

    setSelectedColumn((prev) => {
      if (prev && guideColumns.includes(prev)) return prev
      return guideColumns[0]
    })
  }, [guideColumns, variantGuidesMap, PDPVariant, isEyewear])

  useEffect(() => {
    if (isEyewear) {
      setSelectedColumn(null)
      return
    }

    if (!guideColumns.length) return

    const currentSizeOption =
      PDPVariant?.selectedOptions?.find((option) => option.name.toLowerCase() === 'size')?.value || null

    if (currentSizeOption) {
      const matchIndex = guideColumns.findIndex(
        (column) => column.toLowerCase() === currentSizeOption.toLowerCase(),
      )

      if (matchIndex !== -1) {
        setSelectedColumn(guideColumns[matchIndex])
        return
      }
    }

    setSelectedColumn((prev) => {
      if (prev && guideColumns.includes(prev)) return prev
      return guideColumns[0]
    })
  }, [guideColumns, PDPVariant, isEyewear, activeVariantId, variantGuidesMap])

  useEffect(() => {
    setMeasurementUnit(isEyewear ? 'mm' : 'in')
  }, [isEyewear])

  useEffect(() => {
    if (!isEyewear || !eyewearSizeOptions.length) return

    if (activeVariantId) {
      const activeId = String(activeVariantId)
      const hasMatch = eyewearSizeOptions.some(({ id }) => {
        if (activeId === id) return true
        return activeId.endsWith(`/${id}`)
      })

      if (hasMatch) {
        return
      }
    }

    const fallbackOption = eyewearSizeOptions[0]
    if (fallbackOption) {
      setActiveVariantId(String(fallbackOption.id))
    }
  }, [activeVariantId, eyewearSizeOptions, isEyewear])

  const selectedColumnIndex = useMemo(() => {
    if (!selectedColumn) return 0
    const index = guideColumns.indexOf(selectedColumn)
    return index >= 0 ? index : 0
  }, [guideColumns, selectedColumn])

  const formatNumber = useCallback((value: number, fractionDigits: number) => {
    const fixed = value.toFixed(fractionDigits)
    return fixed.replace(/\.?0+$/, '')
  }, [])

  const parseMeasurementValue = useCallback((value: string) => {
    const trimmed = value?.trim()

    if (!trimmed) {
      return { raw: '', inches: null as number | null, mm: null as number | null }
    }

    const normalized = trimmed.replace(/\s+/g, '')
    const numericMatch = normalized.match(/-?\d*\.?\d+/)
    const numeric = numericMatch ? Number.parseFloat(numericMatch[0]) : Number.NaN

    if (Number.isNaN(numeric)) {
      return { raw: trimmed, inches: null as number | null, mm: null as number | null }
    }

    const lower = trimmed.toLowerCase()

    if (lower.includes('mm')) {
      const mmValue = numeric
      return { raw: trimmed, inches: mmValue / 25.4, mm: mmValue }
    }

    if (lower.includes('cm')) {
      const cmValue = numeric
      const inchesValue = cmValue / 2.54
      return { raw: trimmed, inches: inchesValue, mm: inchesValue * 25.4 }
    }

    const inchesValue = numeric
    return { raw: trimmed, inches: inchesValue, mm: inchesValue * 25.4 }
  }, [])

  const formatMeasurement = useCallback(
    (value: string, unit: 'in' | 'mm') => {
      const parsed = parseMeasurementValue(value)

      if (unit === 'mm') {
        if (parsed.mm === null) {
          return value ? (value.toLowerCase().includes('mm') ? value : `${value} mm`) : ''
        }

        const formatted = formatNumber(parsed.mm, parsed.mm >= 100 ? 0 : 1)
        return `${formatted} mm`
      }

      if (parsed.inches === null) {
        return value ? (value.includes('"') || value.toLowerCase().includes('in') ? value : `${value} in`) : ''
      }

      const formatted = formatNumber(parsed.inches, parsed.inches % 1 === 0 ? 0 : 2)

      return `${formatted} in`
    },
    [formatNumber, parseMeasurementValue],
  )

  const parseEyewearValue = useCallback((value: string | number | null | undefined) => {
    if (value === null || value === undefined) return null
    const parsed = Number.parseFloat(String(value))
    return Number.isFinite(parsed) ? parsed : null
  }, [])

  const formatEyewearMeasurement = useCallback((minValue: string | number | null | undefined, maxValue?: string | number | null) => {
    const min = parseEyewearValue(minValue)
    const max = parseEyewearValue(maxValue)

    if (min === null && max === null) {
      return { mm: '—', in: '—' }
    }

    const buildInches = (value: number) => {
      const inches = value / 25.4
      return `${formatNumber(inches, inches % 1 === 0 ? 0 : 2)} in`
    }

    if (min !== null && max !== null) {
      return {
        mm: `${formatNumber(min, 0)} - ${formatNumber(max, 0)} mm`,
        in: `${buildInches(min)} - ${buildInches(max)}`,
      }
    }

    const value = (min ?? max) as number
    return {
      mm: `${formatNumber(value, 0)} mm`,
      in: buildInches(value),
    }
  }, [formatNumber, parseEyewearValue])

  const measurementList = useMemo(() => {
    if (isEyewear) {
      const measurements: { label: string; mm: string; in: string }[] = []

      const pushMeasurement = (
        label: string,
        minValue: string | number | null | undefined,
        maxValue?: string | number | null,
      ) => {
        const formatted = formatEyewearMeasurement(minValue, maxValue)
        if (formatted.mm === '—' && formatted.in === '—') return
        measurements.push({ label, mm: formatted.mm, in: formatted.in })
      }

      pushMeasurement('Frame Height', sizeGuideData?.frameHeight)
      pushMeasurement('Frame Width', sizeGuideData?.frameWidth)
      pushMeasurement('Bridge Width', sizeGuideData?.bridgeWidth)
      pushMeasurement('Temple Length', sizeGuideData?.templeLength, sizeGuideData?.templeLengthMax)

      return measurements
    }

    return guideLabels.map((label, rowIndex) => {
      const rowValues = parsedEntrySizes[rowIndex] ?? []
      const rawValue = rowValues[selectedColumnIndex] ?? ''
      return {
        label,
        rawValue,
      }
    })
  }, [
    formatEyewearMeasurement,
    guideLabels,
    isEyewear,
    parsedEntrySizes,
    sizeGuideData?.bridgeWidth,
    sizeGuideData?.frameHeight,
    sizeGuideData?.frameWidth,
    sizeGuideData?.templeLength,
    sizeGuideData?.templeLengthMax,
    selectedColumnIndex
  ])

  const eyewearGuidelineVisibility = useMemo(() => {
    const defaults = {
      frameWidth: false,
      frameHeight: false,
      bridgeWidth: false,
      templeLength: false,
    }

    if (!isEyewear || measurementList.length === 0) {
      return defaults
    }

    const normalizeLabel = (value: string) => value.trim().toLowerCase()
    const labelSet = new Set<string>()

    measurementList.forEach((measurement) => {
      const label = (measurement as { label?: string }).label
      if (!label) return
      labelSet.add(normalizeLabel(label))
    })

    const hasLabel = (label: string) => labelSet.has(normalizeLabel(label))

    return {
      frameWidth: hasLabel('Frame Width'),
      frameHeight: hasLabel('Frame Height'),
      bridgeWidth: hasLabel('Bridge Width'),
      templeLength: hasLabel('Temple Length'),
    }
  }, [isEyewear, measurementList])

  const normalizeFitLabel = useCallback((value: string | null | undefined) => {
    if (!value) return ''
    return value.toLowerCase().replace(/[^a-z]/g, '')
  }, [])

  const activeFitIndex = useMemo(() => {
    if (!guideFitOptions.length) return -1

    const target = normalizeFitLabel(guideFit)

    if (!target) return -1

    const matchIndex = guideFitOptions.findIndex((option) => normalizeFitLabel(option) === target)

    return matchIndex >= 0 ? matchIndex : -1
  }, [guideFit, guideFitOptions, normalizeFitLabel])

  const displayActiveFitIndex = useMemo(() => {
    if (!guideFitOptions.length) return -1
    if (activeFitIndex >= 0) return activeFitIndex
    return Math.floor(guideFitOptions.length / 2)
  }, [activeFitIndex, guideFitOptions])

  useEffect(() => {
    const sizeGuideTriggers = Array.from(document.querySelectorAll<HTMLElement>(TRIGGER_SELECTOR))

    if (!sizeGuideTriggers.length) return

    const handleTriggerClick = (event: Event) => {
      event.preventDefault()
      setIsDrawerOpen(true)
    }

    sizeGuideTriggers.forEach((trigger) => trigger.addEventListener('click', handleTriggerClick))

    return () => {
      sizeGuideTriggers.forEach((trigger) => trigger.removeEventListener('click', handleTriggerClick))
    }
  }, [])

  useEffect(() => {
    const portalNode = document.createElement('div')

    portalNode.setAttribute('data-size-guide-drawer-portal', '')
    document.body.appendChild(portalNode)
    setPortalElement(portalNode)

    return () => {
      document.body.removeChild(portalNode)
    }
  }, [])

  useEffect(() => {
    if (!isDrawerOpen) {
      document.body.classList.remove('overflow-hidden')
      return
    }

    document.body.classList.add('overflow-hidden')
    window.requestAnimationFrame(() => panelRef.current?.focus())

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDrawerOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.classList.remove('overflow-hidden')
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isDrawerOpen])

  const drawerRootClasses = ['fixed inset-0 z-40 flex justify-end overflow-hidden transition', isDrawerOpen ? 'pointer-events-auto' : 'pointer-events-none'].filter(Boolean).join(' ')
  const overlayClasses = ['absolute inset-0 bg-black/60 transition-opacity duration-300 ease-out', isDrawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'].filter(Boolean).join(' ')
  const panelClasses = ['fixed top-0 flex h-full w-full max-w-[450px] flex-col bg-white shadow-2xl transition-[right] duration-500 ease-in-out', isDrawerOpen ? 'right-0 pointer-events-auto translate-x-0' : '-right-full pointer-events-none translate-x-0'].filter(Boolean).join(' ')

  const drawerMarkup = (
    <div
      id="size-guide-drawer-pdp"
      data-state={isDrawerOpen ? 'open' : 'closed'}
      aria-hidden={isDrawerOpen ? 'false' : 'true'}
      className={drawerRootClasses}
    >
      <div
        role="presentation"
        data-size-guide-drawer-overlay
        className={overlayClasses}
        onClick={() => setIsDrawerOpen(false)}
      ></div>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className={panelClasses}
      >
        <div className="section-inner flex h-full flex-col relative rounded-b-lg pdp-gallery-modal mac-scroll flex-1 overflow-y-auto pb-8">
          <div className="flex justify-between sticky top-0 left-0 right-0 w-full bg-white z-20 px-[15px] py-[22px] border-2 border-solid border-black/10 shadow-[0_12px_16px_-4px_rgba(0,0,0,0.08),0_4px_6px_-2px_rgba(0,0,0,0.03)] rounded-bl-[20px] rounded-br-[20px] border-t-0 text-center">
            <div className="flex items-center justify-center gap-[18px]">
              {titleIconSrc ? (
                <span className="relative flex h-11 w-9 items-center justify-center">
                  <img
                    src={titleIconSrc}
                    alt=""
                    className="h-full w-full object-contain"
                    aria-hidden="true"
                  />
                </span>
              ) : null}
              <h2 className="text-[36px] leading-[43px] h1 text-black">{heading}</h2>
            </div>
            <button
              type="button"
              aria-label="Close size guide"
              data-size-guide-drawer-close
              className="text-black transition hover:bg-grey-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-black"
              onClick={() => setIsDrawerOpen(false)}
            >
              <span aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" width="26" height="25" viewBox="0 0 26 25" fill="none">
                  <line x1="2.06066" y1="1.93934" x2="24.0607" y2="23.9393" stroke="black" stroke-width="3"/>
                  <line y1="-1.5" x2="31.1127" y2="-1.5" transform="matrix(-0.707107 0.707107 0.707107 0.707107 24.6923 3)" stroke="black" stroke-width="3"/>
                </svg>
              </span>
            </button>
          </div>

          <div className="relative pt-[35px] px-4 lg:px-[18px]">
            <div className="flex flex-col gap-4 text-left">
              {productNiceTitle ? <h3 className="text-[28px] leading-[36px] font-bold h2 text-black">{productNiceTitle}</h3> : null}

              {!isEyewear && guideColumns.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {guideColumns.map((column) => {
                    const isActive = selectedColumn === column
                    return (
                      <button
                        key={column}
                        type="button"
                        className={[
                          'grid grid-rows-[auto,1fr] items-center justify-center text-[12px] p-1 border rounded-md h-[30px] min-w-[52px] max-w-[105px] shadow-thumbnail hover:border-red bg-gradient-model-grey text-black relative',
                          isActive ? 'bg-gradient-primary-metalic text-white' : 'bg-gradient-model-grey text-black',
                        ].filter(Boolean).join(' ')}
                        onClick={() => setSelectedColumn(column)}
                      >
                        <span className='text-sm font-medium whitespace-normal line-clamp-2'>{column}</span>
                      </button>
                    )
                  })}
                </div>
              ) : null}

              {isEyewear && eyewearSizeOptions.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {eyewearSizeOptions.map((option) => {
                    const activeId = activeVariantId ? String(activeVariantId) : ''
                    const isActive = activeId === option.id || (!!activeId && activeId.endsWith(`/${option.id}`))

                    return (
                      <button
                        key={option.id}
                        type="button"
                        className={[
                          'grid grid-rows-[auto,1fr] items-center justify-center text-[12px] p-1 border rounded-md h-[30px] min-w-[52px] max-w-[105px] shadow-thumbnail hover:border-red bg-gradient-model-grey text-black relative',
                          isActive ? 'bg-gradient-primary-metalic text-white' : 'bg-gradient-model-grey text-black',
                          option.available ? '' : 'opacity-60',
                        ].filter(Boolean).join(' ')}
                        onClick={() => {
                          if (option.id) {
                            setActiveVariantId(String(option.id))
                          }
                        }}
                        aria-pressed={isActive}
                      >
                        <span className="text-sm font-medium whitespace-normal line-clamp-2">{option.label}</span>
                      </button>
                    )
                  })}
                </div>
              ) : null}

              <div className="flex items-center gap-3">
                <div className="relative inline-flex">
                  <div className="relative flex items-center gap-2">
                    <button
                      type="button"
                      className="relative order-2 flex h-[18px] w-[31px] items-center rounded-[40px] bg-gradient-to-b from-[#6ED9FB] to-[#327AC8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
                      onClick={handleMeasurementIndicatorToggle}
                      aria-label={measurementToggleIndicatorAriaLabel}
                    >
                      <span
                        className={[
                          'pointer-events-none absolute left-[4px] top-[3px] h-[12px] w-[12px] rounded-full bg-white transition-transform duration-300 ease-in-out',
                          resolvedMeasurementToggleActiveIndex === 0 ? 'translate-x-0' : 'translate-x-[12px]',
                        ].filter(Boolean).join(' ')}
                      />
                    </button>

                    {measurementToggleUnits.map((unit, index) => {
                      const isActive = measurementUnit === unit
                      return (
                        <button
                          key={unit}
                          type="button"
                          className={[
                            'relative z-10 flex-1 text-center text-black text-[14px] leading-[14px] font-semibold',
                            index === 0 ? 'order-1' : 'order-2',
                            isActive ? 'opacity-100' : 'opacity-40',
                          ].filter(Boolean).join(' ')}
                          onClick={() => setMeasurementUnit(unit)}
                          aria-pressed={isActive}
                        >
                          {unit}
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>

              {measurementList.length > 0 ? (
                <dl
                  className={[
                    'grid gap-[10px] items-start justify-between',
                    measurementList.length < 4 ? 'grid-cols-3' : 'grid-cols-2',
                  ].filter(Boolean).join(' ')}
                >
                  {measurementList.map((measurement, index) => {
                    const label = (measurement as { label: string }).label
                    if (!label) return null
                    return (
                      <div
                        key={`${label}-${index}`}
                        className="flex items-baseline justify-between gap-[2px] lg:gap-[5px]"
                      >
                        <dt className="text-[13px] leading-none font-semibold text-black tracking-[-0.60px]">{label == 'Chest Circumference' ? 'Chest Width' : label}:</dt>
                        <dd className="text-[13px] leading-none text-black tracking-[-0.60px]">
                          {isEyewear
                            ? measurementUnit === 'mm'
                              ? (measurement as { mm: string }).mm
                              : (measurement as { in: string }).in
                            : formatMeasurement((measurement as { rawValue: string }).rawValue, measurementUnit) || '—'}
                        </dd>
                      </div>
                    )
                  })}
                </dl>
              ) : null}

              {guideImage ? (
                <div className="flex flex-col items-center gap-6 px-5">
                  <div className={guideImageWrapperClass}>
                    <img
                      src={guideImage}
                      alt={guideImageAlt}
                      className={`absolute inset-0 h-full w-full object-cover object-center ${isGoggle && 'p-8'}`}
                    />

                    {isEyewear ? (
                      <div className='guidelines'>
                        {eyewearGuidelineVisibility.frameWidth ? (
                          <div className='absolute top-0 inset-x-0 mx-auto text-center text-[12px] italic z-10'>
                            Frame Width
                            <div className='relative h-[1px] w-[90%] m-auto bg-black before:w-[2px] before:h-[10px] before:block before:bg-black after:w-[2px] after:h-[10px] after:block after:bg-black after:absolute after:right-0 after:-bottom-[10px]'></div>
                          </div>
                        ) : null}

                        {eyewearGuidelineVisibility.frameHeight ? (
                          <div className='absolute w-2/5 text-center text-[12px] italic right-[-78px] lg:right-[-60px] top-[84px] lg:top-[102px] rotate-90'>
                            Frame Height
                            <div className='relative h-[1px] w-[90%] m-auto bg-black before:w-[2px] before:h-[10px] before:block before:bg-black after:w-[2px] after:h-[10px] after:block after:bg-black after:absolute after:right-0 after:-bottom-[10px]'></div>
                          </div>
                        ) : null}

                        {eyewearGuidelineVisibility.bridgeWidth ? (
                          <div className='absolute bottom-0 inset-x-0 mx-auto text-center text-[12px] italic'>
                            <div className='rotate-180 h-[1px] w-[100px] m-auto bg-black before:w-[2px] before:h-[10px] before:block before:bg-black after:w-[2px] after:h-[10px] after:block after:bg-black after:absolute after:right-0 after:-bottom-[10px]'></div>
                            Bridge Width
                          </div>
                        ) : null}
                      </div>
                    ) : null }
                  </div>

                  {isEyewear && secondaryGuideImage && sizeGuideData?.templeLength ? (
                    <div className={guideImageWrapperClass}>
                      <img
                        src={secondaryGuideImage}
                        alt={secondaryGuideImageAlt}
                        className="absolute inset-0 h-full w-full object-cover object-center"
                      />

                      {eyewearGuidelineVisibility.templeLength ? (
                        <div className='guidelines'>
                          <div className='absolute top-0 inset-x-0 mx-auto text-center text-[12px] italic z-10'>
                            Temple Length
                            <div className='relative h-[1px] w-[80%] ml-[15px] bg-black before:w-[2px] before:h-[10px] before:block before:bg-black after:w-[2px] after:h-[10px] after:block after:bg-black after:absolute after:right-0 after:-bottom-[10px]'></div>
                          </div>
                        </div>
                      ) : null}

                    </div>
                  ) : null }
                </div>
              ) : null}

              {guideFitOptions.length > 0 ? (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-black mb-[70px]">The Size Spectrum</h3>
                  <div className="relative flex items-start justify-between">
                    {guideFitOptions.map((option, index) => {
                      const isActive = index === displayActiveFitIndex
                      const normalized = normalizeFitLabel(option)
                      const shouldShowIcon =
                        normalized === normalizeFitLabel('Run Small') ||
                        normalized === normalizeFitLabel('Run Large') ||
                        normalized === normalizeFitLabel('Narrowest') ||
                        normalized === normalizeFitLabel('Widest') ||
                        isActive

                      const iconSrc = shouldShowIcon ? fallbackFitIcon : null

                      return (
                        <div
                          key={`${option}-${index}`}
                          className={`relative z-10 flex flex-1 flex-col items-center text-center`}
                        >

                          <span
                            className={[
                              `flex h-[7px] w-full items-center justify-center border transition border border-black/10 [border-width:0.5px] bg-gradient-to-b from-[#6ED9FB] to-[#327AC8]`,
                              isActive ? 'flex' : 'flex',
                              index === 0 ? 'rounded-tl-[8px] rounded-bl-[8px]' : '',
                              index === (guideFitOptions.length - 1) ? 'rounded-tr-[8px] rounded-br-[8px]' : ''
                            ].filter(Boolean).join(' ')}
                          >
                          </span>

                          {iconSrc ? (
                            <div className={isActive ? 'absolute top-[-52px] w-[60px] h-[62px] bg-cover bg-no-repeat pt-[7px]' : ''}
                            style={
                              isActive
                                ? {
                                    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='59' height='59' viewBox='0 0 59 59' fill='none'%3E%3Cg filter='url(%23filter0_d_325_335)'%3E%3Cmask id='path-1-inside-1_325_335' fill='white'%3E%3Cpath d='M32.5918 50.5576C31.4342 52.5421 28.5658 52.5421 27.4082 50.5576L23 43H12C7.58172 43 4 39.4183 4 35V10C4 5.58172 7.58172 2 12 2H47C51.4183 2 55 5.58172 55 10V35C55 39.4183 51.4183 43 47 43H37L32.5918 50.5576Z'/%3E%3C/mask%3E%3Cpath d='M32.5918 50.5576C31.4342 52.5421 28.5658 52.5421 27.4082 50.5576L23 43H12C7.58172 43 4 39.4183 4 35V10C4 5.58172 7.58172 2 12 2H47C51.4183 2 55 5.58172 55 10V35C55 39.4183 51.4183 43 47 43H37L32.5918 50.5576Z' fill='white'/%3E%3Cpath d='M32.5918 50.5576L34.3194 51.5654L34.3194 51.5653L32.5918 50.5576ZM27.4082 50.5576L25.6806 51.5653L25.6806 51.5654L27.4082 50.5576ZM23 43L24.7276 41.9923L24.1488 41H23V43ZM12 2V0V2ZM47 43V45V43ZM37 43V41H35.8512L35.2724 41.9923L37 43ZM32.5918 50.5576L30.8642 49.5499C30.4784 50.2114 29.5216 50.2114 29.1358 49.5499L27.4082 50.5576L25.6806 51.5654C27.61 54.8729 32.39 54.8729 34.3194 51.5654L32.5918 50.5576ZM27.4082 50.5576L29.1358 49.5499L24.7276 41.9923L23 43L21.2724 44.0077L25.6806 51.5653L27.4082 50.5576ZM23 43V41H12V43V45H23V43ZM12 43V41C8.68629 41 6 38.3137 6 35H4H2C2 40.5228 6.47715 45 12 45V43ZM4 35H6V10H4H2V35H4ZM4 10H6C6 6.68629 8.68629 4 12 4V2V0C6.47715 2.38419e-07 2 4.47715 2 10H4ZM12 2V4H47V2V0H12V2ZM47 2V4C50.3137 4 53 6.68629 53 10H55H57C57 4.47715 52.5228 0 47 0V2ZM55 10H53V35H55H57V10H55ZM55 35H53C53 38.3137 50.3137 41 47 41V43V45C52.5228 45 57 40.5228 57 35H55ZM47 43V41H37V43V45H47V43ZM37 43L35.2724 41.9923L30.8642 49.5499L32.5918 50.5576L34.3194 51.5653L38.7276 44.0077L37 43Z' fill='%236ED9FB' mask='url(%23path-1-inside-1_325_335)'/%3E%3C/g%3E%3Cdefs%3E%3Cfilter id='filter0_d_325_335' x='0' y='0' width='59' height='58.046' filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'%3E%3CfeFlood flood-opacity='0' result='BackgroundImageFix'/%3E%3CfeColorMatrix in='SourceAlpha' type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0' result='hardAlpha'/%3E%3CfeOffset dy='2'/%3E%3CfeGaussianBlur stdDeviation='2'/%3E%3CfeComposite in2='hardAlpha' operator='out'/%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0'/%3E%3CfeBlend mode='normal' in2='BackgroundImageFix' result='effect1_dropShadow_325_335'/%3E%3CfeBlend mode='normal' in='SourceGraphic' in2='effect1_dropShadow_325_335' result='shape'/%3E%3C/filter%3E%3C/defs%3E%3C/svg%3E\")"
                                  }
                                : undefined
                              }
                            >
                              <img
                                src={iconSrc}
                                alt={option}
                                className={[`object-contain`,
                                  isActive && `h-8 w-8  mx-auto`,
                                  index === 0 ? `h-14 w-6`: ' ',
                                  index === (guideFitOptions.length - 1) ? 'h-14 w-16' : ' '
                                ].filter(Boolean).join(' ')}
                                  ///*{isActive ? 'h-8 w-8  mx-auto' : 'mt-3 h-8 w-8 object-contain'}*/}
                                // style={index === 0 ? { padding: '2px' } : undefined }
                                loading="lazy"
                              />
                            </div>
                          ) : null}

                          {iconSrc && isActive ? (
                            <img
                              src={iconSrc}
                              alt={option}
                              className={[`object-contain`,
                                index === 0 ? 'h-14 w-6': '',
                                index === (guideFitOptions.length - 1) ? 'h-14 w-16' : '',
                                isActive && index != 0 &&  index != (guideFitOptions.length - 1) && 'h-14 w-10'
                              ].filter(Boolean).join(' ')}
                              loading="lazy"
                            />
                          ) : null}

                          <span
                            className={[
                              'text-[11px] leading-[14px] text-grey-6 italic',
                              isActive ? 'text-black' : '',
                              shouldShowIcon ? '' : 'hidden'
                            ].filter(Boolean).join(' ')}
                          >
                            {option}
                          </span>
                        </div>
                      )
                    })}
                  </div>

                  {drawerCtaText && isEyewear ? (
                    <div className="pt-2">
                      <a
                        href={drawerCtaLink}
                        className={`button w-full button--${drawerCtaVariant}`}
                        onClick={(event) => {
                          if (!drawerCtaLink || drawerCtaLink === '#') {
                            event.preventDefault()
                            setIsDrawerOpen(false)
                            if (typeof window !== 'undefined') {
                              window.setTimeout(() => {
                                window.dispatchEvent(
                                  new CustomEvent('size-guide:open-modal', {
                                    detail: { isEyewear },
                                  })
                                )
                              }, 0)
                            }
                          }
                        }}
                      >
                        <div className="rectangle-1"></div>
                        <div className="button-text">{drawerCtaText}</div>
                      </a>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {drawerCarouselMedia.length > 0 ? (
                <div className="relative space-y-4 h-[380px] mt-5">
                  <div className="h-full">
                    <Swiper
                      className="h-full"
                      slidesPerView={1}
                      spaceBetween={16}
                      navigation={{
                        nextEl: `.${mediaNextClass}`,
                        prevEl: `.${mediaPrevClass}`,
                      }}
                      initialSlide={1}
                      loop={false}
                      allowTouchMove
                    >
                      {drawerCarouselMedia.map((media, index) => (
                        <SwiperSlide key={media.id ?? index} className="h-full">
                          <div className='absolute z-2 top-0 left-0 right-0 bg-gradient-metal border-grey-2 rounded-tl-xl rounded-tr-xl'>
                            <div className="flex items-center gap-x-1 py-1.5 px-1 md:p-1.5">
                              <ColorDot colorType="gray" />
                              <ColorDot colorType="gray" />
                              <ColorDot colorType="green" />
                              {media.alt && (
                                <p className="body-xxs font-medium absolute inset-0 flex items-center justify-center text-center">
                                  {media.alt.split("|")[1] ? media.alt.split("|")[1].split('').slice(0, 32) : productNiceTitle}
                                </p>
                              )}
                            </div>
                          </div>
                          <img
                            src={media.src}
                            alt={media.alt || productNiceTitle}
                            className="h-full w-full rounded-bl-xl rounded-br-xl object-cover pt-[22px]"
                            loading="lazy"
                          />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </div>
                  <div className="flex items-center justify-between absolute top-1/2 left-1/2 w-[calc(100%-16px)] mx-auto z-10 -translate-x-1/2 -translate-y-1/2 transform">
                    <button
                      type="button"
                      aria-label="Previous image"
                      className={`${mediaPrevClass} flex h-12 w-12 items-center justify-center rounded-full bg-white/50 text-black transition hover:bg-grey-1`}
                    >
                      <Icon name="carousel-arrow-left" />
                    </button>
                    <button
                      type="button"
                      aria-label="Next image"
                      className={`${mediaNextClass} flex h-12 w-12 items-center justify-center rounded-full bg-white/50 text-black transition hover:bg-grey-1`}
                    >
                      <Icon name="carousel-arrow-right" />
                    </button>
                  </div>
                </div>
              ) : null}

              <div className='flex text-[12px] leading-[14px] opacity-60 text-center max-w-[332px] mt-3 mx-auto'>
                <p>{headingTextarea}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  if (!portalElement) {
    return null
  }

  return createPortal(drawerMarkup, portalElement)
}

export default SizeGuideDrawer
